import type {
  DesktopFeatureChannelPolicy,
  DesktopFeaturePolicy,
  DesktopFeatureVersionRange,
  DesktopUpdateApproval,
  DesktopUpdateProduct,
  PolicyUserIdentity,
} from '@/types';

export const DESKTOP_UPDATE_PRODUCTS = ['tutti-desktop'] as const;

export function isCatalogDesktopUpdateProduct(product: string) {
  return (DESKTOP_UPDATE_PRODUCTS as readonly string[]).includes(product);
}
export const DESKTOP_UPDATE_PLATFORMS = ['macos', 'windows'] as const;
export const DESKTOP_UPDATE_ARCHITECTURES = ['arm64', 'x64'] as const;
export const DESKTOP_UPDATE_CHANNELS = ['stable', 'rc'] as const;
export const DESKTOP_UPDATE_TARGETS = DESKTOP_UPDATE_PLATFORMS.flatMap(
  (platform) =>
    DESKTOP_UPDATE_ARCHITECTURES.map((architecture) => ({
      platform,
      architecture,
      key: desktopUpdateTargetKey(platform, architecture),
    })),
);

export function desktopUpdateTargetKey(platform: string, architecture: string) {
  return `${platform}/${architecture}`;
}

export function activeDesktopUpdateApproval(
  approvals: DesktopUpdateApproval[],
  product: string,
) {
  return approvals.find(
    (approval) =>
      approval.product === product &&
      (approval.status === 'pending' || approval.status === 'approved'),
  );
}

export function desktopUpdateUserLabel(
  identities: Record<string, PolicyUserIdentity>,
  userID: string,
) {
  const identity = identities[userID];
  const name = identity?.name.trim() || '';
  const email = identity?.email.trim() || '';
  if (name && email) {
    return `${name}（${email}）`;
  }
  return name || email || '用户信息不可用';
}

export function cloneDesktopUpdateProduct(
  value: DesktopUpdateProduct,
): DesktopUpdateProduct {
  return {
    ...value,
    releaseCapabilities: value.releaseCapabilities.map((item) => ({
      platform: item.platform,
      architectures: [...item.architectures],
    })),
    channels: {
      stable: {
        ...value.channels.stable,
        targetOverrides: {
          ...value.channels.stable.targetOverrides,
        },
      },
      rc: {
        ...value.channels.rc,
        targetOverrides: { ...value.channels.rc.targetOverrides },
      },
    },
    featureAllowlist: {
      features: (value.featureAllowlist?.features || []).map((feature) => ({
        ...feature,
        channels: {
          stable: cloneDesktopFeatureChannel(feature.channels.stable),
          rc: cloneDesktopFeatureChannel(feature.channels.rc),
        },
      })),
    },
  };
}

function cloneDesktopFeatureChannel(
  channel: DesktopFeatureChannelPolicy,
): DesktopFeatureChannelPolicy {
  return {
    defaultRange: channel.defaultRange
      ? { ...channel.defaultRange }
      : undefined,
    targetOverrides: Object.fromEntries(
      Object.entries(channel.targetOverrides).map(([target, override]) => [
        target,
        {
          ...override,
          range: override.range ? { ...override.range } : undefined,
        },
      ]),
    ),
  };
}

export function createEmptyDesktopFeature(): DesktopFeaturePolicy {
  return {
    key: '',
    description: '',
    channels: {
      stable: { targetOverrides: {} },
      rc: { targetOverrides: {} },
    },
  };
}

export function isDesktopUpdateReleaseAvailable(
  product: DesktopUpdateProduct,
  platform: string,
  architecture: string,
) {
  return product.releaseCapabilities.some(
    (capability) =>
      capability.platform === platform &&
      capability.architectures.includes(architecture),
  );
}

export function effectiveDesktopMinimumVersion(
  product: DesktopUpdateProduct,
  channel: 'stable' | 'rc',
  platform: string,
  architecture: string,
) {
  return (
    product.channels[channel].targetOverrides[
      desktopUpdateTargetKey(platform, architecture)
    ] ||
    product.channels[channel].defaultMinimumVersion ||
    '未设置'
  );
}

export function buildDesktopUpdateChangePreview(
  current: DesktopUpdateProduct,
  draft: DesktopUpdateProduct,
) {
  const differences: string[] = [];
  const affectedTargets = new Set<string>();
  for (const channel of DESKTOP_UPDATE_CHANNELS) {
    if (
      current.channels[channel].defaultMinimumVersion !==
      draft.channels[channel].defaultMinimumVersion
    ) {
      differences.push(
        `${channel} 统一版本：${current.channels[channel].defaultMinimumVersion || '未设置'} → ${draft.channels[channel].defaultMinimumVersion || '未设置'}`,
      );
    }
    for (const target of DESKTOP_UPDATE_TARGETS) {
      const before = effectiveDesktopMinimumVersion(
        current,
        channel,
        target.platform,
        target.architecture,
      );
      const after = effectiveDesktopMinimumVersion(
        draft,
        channel,
        target.platform,
        target.architecture,
      );
      if (before !== after) {
        differences.push(
          `${channel}/${target.key} 生效版本：${before} → ${after}`,
        );
        affectedTargets.add(target.key);
        continue;
      }
      const beforeOverride =
        current.channels[channel].targetOverrides[target.key] || '';
      const afterOverride =
        draft.channels[channel].targetOverrides[target.key] || '';
      if (beforeOverride !== afterOverride) {
        differences.push(
          `${channel}/${target.key} 覆盖配置：${beforeOverride || '继承统一版本'} → ${afterOverride || '继承统一版本'}（生效版本不变）`,
        );
        affectedTargets.add(target.key);
      }
    }
  }
  const currentFeatures = new Map(
    (current.featureAllowlist?.features || []).map((feature) => [
      feature.key,
      feature,
    ]),
  );
  const draftFeatures = new Map(
    (draft.featureAllowlist?.features || []).map((feature) => [
      feature.key,
      feature,
    ]),
  );
  for (const [key, feature] of currentFeatures) {
    if (!draftFeatures.has(key)) {
      differences.push(`Feature 删除：${key}`);
      for (const target of DESKTOP_UPDATE_TARGETS) {
        affectedTargets.add(target.key);
      }
      continue;
    }
    const next = draftFeatures.get(key);
    if (next && feature.description !== next.description) {
      differences.push(`Feature ${key} 描述已修改`);
    }
  }
  for (const [key, feature] of draftFeatures) {
    const before = currentFeatures.get(key);
    if (!before) {
      differences.push(`Feature 新增：${key || '（待填写 key）'}`);
      for (const target of DESKTOP_UPDATE_TARGETS) {
        affectedTargets.add(target.key);
      }
      continue;
    }
    for (const channel of DESKTOP_UPDATE_CHANNELS) {
      for (const target of DESKTOP_UPDATE_TARGETS) {
        const currentRange = effectiveDesktopFeatureRange(
          before,
          channel,
          target.key,
        );
        const draftRange = effectiveDesktopFeatureRange(
          feature,
          channel,
          target.key,
        );
        if (
          formatDesktopFeatureRange(currentRange) !==
          formatDesktopFeatureRange(draftRange)
        ) {
          differences.push(
            `Feature ${key} ${channel}/${target.key}：${formatDesktopFeatureRange(currentRange)} → ${formatDesktopFeatureRange(draftRange)}`,
          );
          affectedTargets.add(target.key);
        }
      }
    }
  }
  return {
    differences,
    affectedTargets: [...affectedTargets],
  };
}

export function effectiveDesktopFeatureRange(
  feature: DesktopFeaturePolicy,
  channel: 'stable' | 'rc',
  target: string,
): DesktopFeatureVersionRange | undefined {
  const policy = feature.channels[channel];
  const override = policy.targetOverrides[target];
  if (override?.mode === 'disabled') {
    return undefined;
  }
  if (override?.mode === 'range') {
    return override.range;
  }
  return policy.defaultRange;
}

export function formatDesktopFeatureRange(
  range: DesktopFeatureVersionRange | undefined,
) {
  if (!range) {
    return '不支持';
  }
  return `[${range.minimumVersionInclusive || '未设置'}, ${
    range.maximumVersionExclusive || '+∞'
  })`;
}

export interface DesktopFeatureApprovalChange {
  key: string;
  kind: 'added' | 'removed' | 'modified';
  description: string;
  details: string[];
}

function desktopFeatureChannelConfiguration(
  feature: DesktopFeaturePolicy,
  channel: 'stable' | 'rc',
) {
  const policy = feature.channels[channel];
  const configuration = [
    `默认 ${formatDesktopFeatureRange(policy.defaultRange)}`,
  ];
  for (const [target, override] of Object.entries(policy.targetOverrides).sort(
    ([left], [right]) => left.localeCompare(right),
  )) {
    configuration.push(
      `${target} ${
        override.mode === 'disabled'
          ? '不支持'
          : formatDesktopFeatureRange(override.range)
      }`,
    );
  }
  return configuration.join(' · ');
}

export function buildDesktopFeatureApprovalChanges(
  baseline: DesktopUpdateProduct,
  target: DesktopUpdateProduct,
): DesktopFeatureApprovalChange[] {
  const baselineFeatures = new Map(
    (baseline.featureAllowlist?.features || []).map((feature) => [
      feature.key,
      feature,
    ]),
  );
  const targetFeatures = new Map(
    (target.featureAllowlist?.features || []).map((feature) => [
      feature.key,
      feature,
    ]),
  );
  const keys = [
    ...new Set([...baselineFeatures.keys(), ...targetFeatures.keys()]),
  ].sort((left, right) => left.localeCompare(right));

  return keys.flatMap<DesktopFeatureApprovalChange>((key) => {
    const before = baselineFeatures.get(key);
    const after = targetFeatures.get(key);
    if (!before && after) {
      return [
        {
          key,
          kind: 'added' as const,
          description: after.description,
          details: DESKTOP_UPDATE_CHANNELS.map(
            (channel) =>
              `${channel.toUpperCase()} ${desktopFeatureChannelConfiguration(after, channel)}`,
          ),
        },
      ];
    }
    if (before && !after) {
      return [
        {
          key,
          kind: 'removed' as const,
          description: before.description,
          details: DESKTOP_UPDATE_CHANNELS.map(
            (channel) =>
              `${channel.toUpperCase()} 原配置 ${desktopFeatureChannelConfiguration(before, channel)}`,
          ),
        },
      ];
    }
    if (!before || !after) {
      return [];
    }

    const details: string[] = [];
    if (before.description !== after.description) {
      details.push(`描述：${before.description} → ${after.description}`);
    }
    for (const channel of DESKTOP_UPDATE_CHANNELS) {
      const beforeConfiguration = desktopFeatureChannelConfiguration(
        before,
        channel,
      );
      const afterConfiguration = desktopFeatureChannelConfiguration(
        after,
        channel,
      );
      if (beforeConfiguration !== afterConfiguration) {
        details.push(
          `${channel.toUpperCase()}：${beforeConfiguration} → ${afterConfiguration}`,
        );
      }
    }
    if (!details.length) {
      return [];
    }
    return [
      {
        key,
        kind: 'modified' as const,
        description: after.description,
        details,
      },
    ];
  });
}

const desktopFeatureKeyPattern = /^[A-Za-z][A-Za-z0-9]*(?:[._-][A-Za-z0-9]+)*$/;
const stableVersionPattern = /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)$/;
const rcVersionPattern =
  /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)-rc\.(0|[1-9]\d*)$/;

function compareDesktopVersions(left: string, right: string) {
  const leftParts = left.replace('-rc.', '.').split('.');
  const rightParts = right.replace('-rc.', '.').split('.');
  for (let index = 0; index < leftParts.length; index += 1) {
    const leftPart = leftParts[index];
    const rightPart = rightParts[index];
    if (leftPart.length !== rightPart.length) {
      return leftPart.length < rightPart.length ? -1 : 1;
    }
    if (leftPart !== rightPart) {
      return leftPart < rightPart ? -1 : 1;
    }
  }
  return 0;
}

function validateDesktopFeatureRange(
  range: DesktopFeatureVersionRange,
  channel: 'stable' | 'rc',
) {
  const pattern =
    channel === 'stable' ? stableVersionPattern : rcVersionPattern;
  if (!pattern.test(range.minimumVersionInclusive)) {
    return `${channel} 下界格式不正确`;
  }
  if (
    range.maximumVersionExclusive &&
    !pattern.test(range.maximumVersionExclusive)
  ) {
    return `${channel} 上界格式不正确`;
  }
  if (
    range.maximumVersionExclusive &&
    compareDesktopVersions(
      range.minimumVersionInclusive,
      range.maximumVersionExclusive,
    ) >= 0
  ) {
    return `${channel} 上界必须严格大于下界`;
  }
  return null;
}

export function validateDesktopFeatureDraft(product: DesktopUpdateProduct) {
  const features = product.featureAllowlist?.features || [];
  if (features.length > 256) {
    return '每个产品最多配置 256 个 Feature';
  }
  const keys = new Set<string>();
  for (const feature of features) {
    if (
      feature.key.length > 128 ||
      !desktopFeatureKeyPattern.test(feature.key)
    ) {
      return 'Feature key 格式不正确';
    }
    if (keys.has(feature.key)) {
      return `Feature key 重复：${feature.key}`;
    }
    keys.add(feature.key);
    if (
      feature.description !== feature.description.trim() ||
      feature.description.length === 0 ||
      [...feature.description].length > 500
    ) {
      return `Feature ${feature.key} 的描述必须为 1–500 个字符且首尾无空格`;
    }
    let hasRange = false;
    for (const channel of DESKTOP_UPDATE_CHANNELS) {
      const policy = feature.channels[channel];
      if (policy.defaultRange) {
        const error = validateDesktopFeatureRange(policy.defaultRange, channel);
        if (error) {
          return `Feature ${feature.key}：${error}`;
        }
        hasRange = true;
      }
      for (const [target, override] of Object.entries(policy.targetOverrides)) {
        if (!DESKTOP_UPDATE_TARGETS.some((item) => item.key === target)) {
          return `Feature ${feature.key} 包含非法目标 ${target}`;
        }
        if (override.mode === 'range') {
          if (!override.range) {
            return `Feature ${feature.key} ${channel}/${target} 缺少范围`;
          }
          const error = validateDesktopFeatureRange(override.range, channel);
          if (error) {
            return `Feature ${feature.key} ${target}：${error}`;
          }
          hasRange = true;
        }
      }
    }
    if (!hasRange) {
      return `Feature ${feature.key} 至少需要一个默认或目标范围`;
    }
  }
  return null;
}
